import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const ModuleFormModal = ({ module, planId, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    moduleCode: '',
    moduleName: '',
    description: '',
    status: 'PENDING',
    positionOrder: 0,
    positionX: '',
    positionY: '',
    positionZ: '',
    totalWidth: '',
    totalHeight: '',
    totalDepth: ''
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (module) {
      setFormData({
        moduleCode: module.module_code || '',
        moduleName: module.module_name || '',
        description: module.description || '',
        status: module.status || 'PENDING',
        positionOrder: module.position_order || 0,
        positionX: module.position_x || '',
        positionY: module.position_y || '',
        positionZ: module.position_z || '',
        totalWidth: module.total_width || '',
        totalHeight: module.total_height || '',
        totalDepth: module.total_depth || ''
      });
    } else {
      setFormData({
        moduleCode: '',
        moduleName: '',
        description: '',
        status: 'PENDING',
        positionOrder: 0,
        positionX: '',
        positionY: '',
        positionZ: '',
        totalWidth: '',
        totalHeight: '',
        totalDepth: ''
      });
    }
  }, [module, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        moduleCode: formData.moduleCode,
        moduleName: formData.moduleName,
        description: formData.description || null,
        status: formData.status,
        positionOrder: parseInt(formData.positionOrder) || 0,
        positionX: formData.positionX ? parseFloat(formData.positionX) : null,
        positionY: formData.positionY ? parseFloat(formData.positionY) : null,
        positionZ: formData.positionZ ? parseFloat(formData.positionZ) : null,
        totalWidth: formData.totalWidth ? parseFloat(formData.totalWidth) : null,
        totalHeight: formData.totalHeight ? parseFloat(formData.totalHeight) : null,
        totalDepth: formData.totalDepth ? parseFloat(formData.totalDepth) : null
      };

      await onSave(module?.id, data);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar módulo:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {module ? 'Editar Módulo' : 'Novo Módulo'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código do Módulo *
              </label>
              <input
                type="text"
                name="moduleCode"
                value={formData.moduleCode}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="MOD-001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="PENDING">Pendente</option>
                <option value="IN_PROGRESS">Em Andamento</option>
                <option value="COMPLETED">Concluído</option>
                <option value="CANCELLED">Cancelado</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Módulo *
            </label>
            <input
              type="text"
              name="moduleName"
              value={formData.moduleName}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Módulo Superior"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Descrição opcional do módulo"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Largura (mm)
              </label>
              <input
                type="number"
                name="totalWidth"
                value={formData.totalWidth}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="1200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Altura (mm)
              </label>
              <input
                type="number"
                name="totalHeight"
                value={formData.totalHeight}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Profundidade (mm)
              </label>
              <input
                type="number"
                name="totalDepth"
                value={formData.totalDepth}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="580"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModuleFormModal;
